import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { trainerApi } from '@/api/trainer'
import { CalendarDays, MapPin } from 'lucide-react'
import { useTrainerDates } from '@/hooks/useDates'

function statusBadge(status: string) {
  const map: Record<string, string> = {
    Booked:    'bg-green-50 text-green-700 border-green-100',
    Completed: 'bg-slate-100 text-slate-600 border-slate-200',
    Rejected:  'bg-amber-50 text-amber-700 border-amber-100',
  }
  return map[status] ?? 'bg-slate-100 text-slate-600 border-slate-200'
}

function BookingCard({
  b,
  basePath,
  dimmed = false,
}: {
  b: ReturnType<typeof trainerApi.getMyBookings> extends Promise<{ data: (infer T)[] }> ? T : never
  basePath: string
  dimmed?: boolean
}) {
  const { formatDate } = useTrainerDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    })
  }
  const isSession = b.kind === 'Session'

  const inner = (
    <>
      <div className="w-14 shrink-0 text-center pt-0.5">
        <p className={`text-xs uppercase tracking-wide ${dimmed ? 'text-gray-400' : 'text-gray-400'}`}>
          {formatDate(b.bookingDate + 'T00:00:00', { month: 'short' })}
        </p>
        <p className={`text-2xl font-bold leading-none ${dimmed ? 'text-gray-400' : 'text-gray-900'}`}>
          {new Date(b.bookingDate + 'T00:00:00').getDate()}
        </p>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium ${dimmed ? 'text-gray-600' : 'text-gray-900'}`}>
          {b.courseName}{b.courseCode ? ` (${b.courseCode})` : ''}
        </p>
        <p className={`text-sm mt-0.5 ${dimmed ? 'text-gray-400' : 'text-gray-500'}`}>
          {isSession ? 'Public session' : b.clientName} · {b.places} place{b.places !== 1 ? 's' : ''}
        </p>
        {b.venueName && (
          <p className={`text-xs mt-0.5 flex items-center gap-1 ${dimmed ? 'text-gray-400' : 'text-gray-400'}`}>
            <MapPin size={11} className="shrink-0" />{b.venueName}
          </p>
        )}
        {!dimmed && (
          <p className="text-xs text-gray-400 mt-0.5">{fmtDate(b.bookingDate)}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(b.status)}`}>
          {b.status}
        </span>
        {b.role && !dimmed && <span className="text-xs text-gray-400">{b.role}</span>}
      </div>
    </>
  )

  const className = `bg-white border border-gray-200 rounded-xl px-5 py-4 flex items-start gap-5 hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors block ${dimmed ? 'opacity-70' : ''}`

  return (
    <Link to={`${basePath}/${isSession ? 'sessions' : 'bookings'}/${b.bookingId}`} className={className}>
      {inner}
    </Link>
  )
}

type Tab = 'upcoming' | 'completed'

export default function TrainerSchedulePage({ basePath = '/trainer' }: { basePath?: string }) {
  const [tab, setTab] = useState<Tab>('upcoming')

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ['trainer-bookings'],
    queryFn: () => trainerApi.getMyBookings().then(r => r.data),
    staleTime: 0,
  })

  const todayStr = useTrainerDates().today()

  // Upcoming: date >= today, any non-cancelled status, soonest first
  const upcoming = bookings
    .filter(b => b.bookingDate.slice(0, 10) >= todayStr)
    .sort((a, b) => a.bookingDate.localeCompare(b.bookingDate))

  // Completed: status === Completed, most recent first (API already returns desc, but be explicit)
  const completed = bookings
    .filter(b => b.status === 'Completed')
    .sort((a, b) => b.bookingDate.localeCompare(a.bookingDate))

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'upcoming',  label: 'Upcoming',  count: upcoming.length },
    { id: 'completed', label: 'Completed', count: completed.length },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">My Schedule</h1>
        <p className="text-sm text-gray-500 mt-0.5">All sessions you're assigned to deliver.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer flex items-center gap-2 border-b-2 -mb-px ${
              tab === t.id
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
            {t.count > 0 && (
              <span className={`text-xs rounded-full px-1.5 py-0.5 font-normal ${tab === t.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : tab === 'upcoming' ? (
        upcoming.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <CalendarDays size={36} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 font-medium">No upcoming sessions</p>
            <p className="text-sm text-gray-400 mt-1">You have no upcoming bookings assigned.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {upcoming.map(b => (
              <BookingCard key={b.bookingId} b={b} basePath={basePath} />
            ))}
          </div>
        )
      ) : (
        completed.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <CalendarDays size={36} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-500 font-medium">No completed sessions yet</p>
            <p className="text-sm text-gray-400 mt-1">Sessions you complete will appear here.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {completed.map(b => (
              <BookingCard key={b.bookingId} b={b} basePath={basePath} dimmed />
            ))}
          </div>
        )
      )}
    </div>
  )
}
